import { useEffect, useState } from "react";
import { Activity, Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";

// Runs a quick reachability check: pass `draft` for unsaved values or `destId` for a saved destination.
export function DestinationTestResult({ streamId, draft, destId, compact }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const draftKey = JSON.stringify(draft || {});

  useEffect(() => { setRes(null); }, [draftKey]);

  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const r = destId
        ? await api.post(`/streams/${streamId}/destinations/${destId}/test`)
        : await api.post(`/streams/${streamId}/destinations/test`, draft);
      setRes(r.data);
    } catch (e) { setRes({ ok: false, checks: [{ label: "Test", ok: false, detail: e?.response?.data?.detail || "Test failed" }] }); }
    finally { setBusy(false); }
  };

  const tid = destId ? `-${destId}` : "";
  return (
    <div className={compact ? "" : "space-y-2"}>
      <button type="button" data-testid={`dest-test-button${tid}`} onClick={run} disabled={busy}
        className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-700 text-gray-200 hover:border-amber-500/50 hover:text-amber-300 inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />} {busy ? "Testing…" : "Test connection"}
      </button>
      {res && (
        <div data-testid={`dest-test-result${tid}`} className={"rounded-lg border p-3 text-xs space-y-1.5 mt-2 " + (res.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5")}>
          <p data-testid={`dest-test-summary${tid}`} className={"font-semibold " + (res.ok ? "text-emerald-300" : "text-red-300")}>{res.ok ? "Looks good — ready to go live" : "Something needs attention"}</p>
          {res.checks.map((c) => (
            <p key={c.label} className="flex items-start gap-1.5 text-gray-300">
              {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-px shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-red-400 mt-px shrink-0" />}
              <span>{c.label}{c.detail && <span className="text-gray-500"> — {c.detail}</span>}</span>
            </p>
          ))}
          {res.note && <p className="text-gray-500 pt-0.5">{res.note}</p>}
        </div>
      )}
    </div>
  );
}
